import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./profile.css";

const Profile = () => {
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");

      if (storedUser) {
        const parsed = JSON.parse(storedUser);

        setUserData({
          ...parsed,
          joinedAt: parsed.joinedAt || parsed.created_at,
        });
      }
    } catch (error) {
      console.error("Failed to load profile:", error);
      setUserData(null);
    }
  }, []);

  const formatDate = (date) => {
    if (!date) return "N/A";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "N/A";
    }

    return parsedDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "N/A";
    }

    return parsedDate.toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  if (!userData) {
    return (
      <div className="profile-container">
        <div className="profile-loading">
          <i className="fa-solid fa-spinner fa-spin"></i>
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  const username = userData.username || "User";
  const email = userData.email || "No email available";
  const rank = userData.rank || "Member";

  return (
    <div className="profile-container">
      <div className="profile-wrapper">

        {/* ================================
            PROFILE HERO
        ================================= */}

        <div className="profile-card profile-hero">

          <div className="profile-avatar">
            {userData.avatar ? (
              <img
                src={userData.avatar}
                alt={`${username}'s avatar`}
              />
            ) : (
              <span>
                {username.charAt(0).toUpperCase()}
              </span>
            )}
          </div>

          <div className="profile-hero-info">
            <span className="profile-label">
              VORTEX NETWORK MEMBER
            </span>

            <h1>{username}</h1>

            <p className="profile-email">
              <i className="fa-solid fa-envelope"></i>
              {email}
            </p>

            <div className="profile-rank">
              <i className="fa-solid fa-shield-halved"></i>
              {rank}
            </div>
          </div>

          <div className="profile-actions">
            <Link to="/settings" className="profile-edit-btn">
              <i className="fa-solid fa-gear"></i>
              Settings
            </Link>
          </div>
        </div>

        {/* ================================
            ACCOUNT INFORMATION
        ================================= */}

        <div className="profile-card">
          <div className="profile-section-header">
            <div>
              <span className="section-label">ACCOUNT</span>
              <h2>Account Information</h2>
            </div>

            <i className="fa-solid fa-user"></i>
          </div>

          <div className="profile-info-grid">

            <div className="info-item">
              <div className="info-icon">
                <i className="fa-solid fa-id-badge"></i>
              </div>

              <div>
                <span>Username</span>
                <strong>{username}</strong>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <i className="fa-solid fa-envelope"></i>
              </div>

              <div>
                <span>Email Address</span>
                <strong>{email}</strong>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <i className="fa-solid fa-calendar"></i>
              </div>

              <div>
                <span>Member Since</span>
                <strong>{formatDate(userData.joinedAt)}</strong>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <i className="fa-solid fa-clock"></i>
              </div>

              <div>
                <span>Last Login</span>
                <strong>
                  {formatDateTime(userData.last_login)}
                </strong>
              </div>
            </div>

          </div>
        </div>

        {/* ================================
            USER STATS
        ================================= */}

        <div className="profile-card">
          <div className="profile-section-header">
            <div>
              <span className="section-label">ACTIVITY</span>
              <h2>Your Statistics</h2>
            </div>

            <i className="fa-solid fa-chart-simple"></i>
          </div>

          <div className="profile-stats">

            <div className="stat-box">
              <div className="stat-icon">
                <i className="fa-solid fa-upload"></i>
              </div>

              <div>
                <h3>{userData.uploads || 0}</h3>
                <p>Uploads</p>
              </div>
            </div>

            <div className="stat-box">
              <div className="stat-icon">
                <i className="fa-solid fa-eye"></i>
              </div>

              <div>
                <h3>{userData.views || 0}</h3>
                <p>Views</p>
              </div>
            </div>

            <div className="stat-box">
              <div className="stat-icon">
                <i className="fa-solid fa-coins"></i>
              </div>

              <div>
                <h3>{userData.coins || 0}</h3>
                <p>Coins</p>
              </div>
            </div>

          </div>
        </div>

        {/* ================================
            SERVER INFORMATION
        ================================= */}

        <div className="profile-card">
          <div className="profile-section-header">
            <div>
              <span className="section-label">VORTEX NETWORK</span>
              <h2>Server Information</h2>
            </div>

            <i className="fa-solid fa-server"></i>
          </div>

          <div className="profile-info-grid">

            <div className="info-item">
              <div className="info-icon">
                <i className="fa-solid fa-ranking-star"></i>
              </div>

              <div>
                <span>Current Rank</span>
                <strong>{rank}</strong>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <i className="fa-solid fa-hourglass-half"></i>
              </div>

              <div>
                <span>Playtime</span>
                <strong>{userData.playtime || "0h"}</strong>
              </div>
            </div>

          </div>
        </div>

        {/* ================================
            CONNECTIONS
        ================================= */}

        <div className="profile-card">
          <div className="profile-section-header">
            <div>
              <span className="section-label">SOCIAL</span>
              <h2>Connections</h2>
            </div>

            <i className="fa-solid fa-link"></i>
          </div>

          <div className="connections-list">

            <div className="connection-item">
              <div className="connection-icon discord">
                <i className="fa-brands fa-discord"></i>
              </div>

              <div>
                <span>Discord</span>
                <strong>
                  {userData.discord || "Not linked"}
                </strong>
              </div>
            </div>

            <div className="connection-item">
              <div className="connection-icon youtube">
                <i className="fa-brands fa-youtube"></i>
              </div>

              <div>
                <span>YouTube</span>
                <strong>
                  {userData.youtube || "Not linked"}
                </strong>
              </div>
            </div>

            <div className="connection-item">
              <div className="connection-icon website">
                <i className="fa-solid fa-globe"></i>
              </div>

              <div>
                <span>Website</span>
                <strong>
                  {userData.website || "Not set"}
                </strong>
              </div>
            </div>

          </div>
        </div>

        {/* ================================
            LOGOUT
        ================================= */}

        <div className="profile-logout">
          <button
            type="button"
            className="logout-btn"
            onClick={handleLogout}
          >
            <i className="fa-solid fa-right-from-bracket"></i>
            Log Out
          </button>
        </div>

      </div>
    </div>
  );
};

export default Profile;